import { fhirClients } from "src-next/shared/api/fhirClient";
import { organizationIdFromReference } from "../../services/resolveOrganizationDisplays";
import { AFFILIATION_REQUEST_EXTENSION_URL } from "../constants";
import { isPendingAffiliationRequest } from "../helpers/affiliationRequest";
import { buildTeleconsultoriaAffiliation } from "../transformers/affiliationTransformers";
import type { AffiliationOrganization } from "../types";
import { AffiliationRuleError, assertCanAffiliate } from "./affiliationRules";
import { AFFILIATION_RESOURCE_TYPE, searchReceivedAffiliations } from "./affiliationSearchService";

/** Pedido aguardando aprovação, visto pela afiliada. */
export interface PendingProviderRequest {
  affiliation: fhir5.OrganizationAffiliation;
  providerName: string;
  /** `period.start` (YYYY-MM-DD): quando o pedido foi feito. */
  requestedOn?: string;
}

/**
 * Pedido da AFILIADA (organização logada = receptora) para uma prestadora.
 * Mesmas regras da afiliação, do ponto de vista de quem recebe (a prestadora
 * precisa ter a extensão ativa). Grava INATIVO e "pending": só vale depois
 * que a prestadora aprovar. Um pedido aguardando por vez.
 */
export async function requestProviderAffiliation(params: {
  provider: AffiliationOrganization;
  receiver: AffiliationOrganization;
}): Promise<fhir5.OrganizationAffiliation> {
  const { affiliations } = await searchReceivedAffiliations(params.receiver.token);
  if (affiliations.some(isPendingAffiliationRequest)) {
    throw new AffiliationRuleError(
      "Sua organização já tem um pedido aguardando aprovação. Cancele-o para escolher outra prestadora.",
    );
  }
  await assertCanAffiliate({
    providerToken: params.provider.token,
    receiverToken: params.receiver.token,
    isNewPair: true,
    requestedBy: "receiver",
  });
  return fhirClients.admin.create<fhir5.OrganizationAffiliation>(
    AFFILIATION_RESOURCE_TYPE,
    buildTeleconsultoriaAffiliation({ ...params, isPendingRequest: true }),
  );
}

/** Pedido da afiliada ainda aguardando a prestadora (no máximo 1). */
export async function findPendingProviderRequest(
  receiverToken: string,
): Promise<PendingProviderRequest | undefined> {
  if (!receiverToken) return undefined;
  const { affiliations, organizationsById } = await searchReceivedAffiliations(receiverToken);
  const affiliation = affiliations.find(isPendingAffiliationRequest);
  if (!affiliation) return undefined;
  const providerToken = organizationIdFromReference(affiliation.organization?.reference);
  return {
    affiliation,
    providerName:
      organizationsById.get(providerToken)?.name ||
      affiliation.organization?.display ||
      "organização prestadora",
    requestedOn: affiliation.period?.start,
  };
}

/**
 * Cancelar (afiliada) ou recusar (prestadora) = apagar o pedido.
 * Só apaga se ainda for um pedido pendente: afiliação já aprovada nunca some por aqui.
 * Apagar (e não marcar) deixa a afiliada pedir de novo, inclusive à mesma prestadora.
 */
export async function discardAffiliationRequest(
  affiliation: fhir5.OrganizationAffiliation,
): Promise<void> {
  if (!affiliation.id || !isPendingAffiliationRequest(affiliation)) {
    throw new AffiliationRuleError("Este pedido não está mais aguardando aprovação.");
  }
  await fhirClients.admin.remove(AFFILIATION_RESOURCE_TYPE, affiliation.id);
}

/**
 * Aprovar (prestadora): mesmas regras de reativar (ex.: a afiliada pode ter
 * sido atendida por outra enquanto esperava), depois ativa e tira a marca de pedido.
 */
export async function approveAffiliationRequest(
  affiliation: fhir5.OrganizationAffiliation,
): Promise<void> {
  if (!affiliation.id || !isPendingAffiliationRequest(affiliation)) {
    throw new AffiliationRuleError("Este pedido não está mais aguardando aprovação.");
  }
  await assertCanAffiliate({
    providerToken: organizationIdFromReference(affiliation.organization?.reference),
    receiverToken: organizationIdFromReference(affiliation.participatingOrganization?.reference),
    ignoreAffiliationId: affiliation.id,
    isNewPair: false,
  });
  const otherExtensions = (affiliation.extension || []).filter(
    (extension) => extension.url !== AFFILIATION_REQUEST_EXTENSION_URL,
  );
  await fhirClients.admin.patch<fhir5.OrganizationAffiliation>(
    AFFILIATION_RESOURCE_TYPE,
    affiliation.id,
    [
      { op: "add", path: "/active", value: true },
      otherExtensions.length
        ? { op: "add", path: "/extension", value: otherExtensions }
        : { op: "remove", path: "/extension" },
    ],
  );
}
